// src/antdviews/component/DynamicContent.tsx

// 用户动态组件

import axios from 'axios';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
    Layout,
    Tabs,
    Timeline,
    Tooltip,
    Collapse,
    Button,
    Card,
    Typography,
    Space,
    Tag,
    Empty,
    Spin,
} from 'antd';
import type { TabsProps, CollapseProps } from 'antd';
import {
    HistoryOutlined,
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons';

import { DataApi, UserMgmtApi } from '../../services/Api'
import { Global } from '../../services/Global'
import { browserUtil } from '../../utils/BrowserUtil'
import { encrypUtil } from '../../utils/EncrypUtil'
import '../../css/antd.timeline.css'
import { timeUtil } from '../../utils/TimeUtil'
import { ROLE } from '../../services/Macro'
import WarnModal from './WarnModal'
import { ERR_CODE } from '../../services/ServerErr'
import { checker } from '../../utils/Checker'
import { Page } from '../../services/Page'
import { ModalAddDateTab } from './ModalAddDateTab'
import { DateTab, TargetKey } from '../../services/Type'

const { Content } = Layout
const { Title } = Typography

const warnModalStyles = {
    content: {
        width: '480px',
        top: '40%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: '-20px',
    },
}

interface DynamicItem {
    opTime: string
    uname: string
    email: string
    api: string
    params: any
    comment: string
}

class DynamicTreeNode {
    date: string
    dynamicList: DynamicItem[]

    constructor(date: string, activeList: DynamicItem[]) {
        this.date = date
        this.dynamicList = activeList
    }

    addDynamic(item: DynamicItem) {
        this.dynamicList.push(item)
    }
}

const TAB_KEY_DATE = {
    ALL: '1',
    TODAY: '2',
    YESTERDAY: '3',
    THIS_WEEK: '4',
    LAST_WEEK: '5',
    THIS_MONTH: '6',
    LAST_MONTH: '7',
}

const FIXED_DATE_ITEMS = [
    { key: TAB_KEY_DATE.ALL, label: 'all' },
    { key: TAB_KEY_DATE.TODAY, label: 'today' },
    { key: TAB_KEY_DATE.YESTERDAY, label: 'yesterday' },
    { key: TAB_KEY_DATE.THIS_WEEK, label: 'this-week' },
    { key: TAB_KEY_DATE.LAST_WEEK, label: 'last-week' },
    { key: TAB_KEY_DATE.THIS_MONTH, label: 'this-month' },
    { key: TAB_KEY_DATE.LAST_MONTH, label: 'last-month' },
]

interface DynamicContentState {
    logPage: number
    logSize: number
    tree: DynamicTreeNode[]
    list: any[]
    totalRecords: number

    showWarn: boolean
    warnTitle: string
    warnInfo: string
    errCode: number

    isModal3Open: boolean
    activeDate: string
}

class _DynamicContent extends React.Component<WithTranslation, DynamicContentState> {
    state: DynamicContentState = {
        logPage: 1,
        logSize: 100,
        tree: [],
        list: [],
        totalRecords: 0,

        showWarn: false,
        warnTitle: '本地错误',
        warnInfo: '参数错误',
        errCode: 0,

        isModal3Open: false,
        activeDate: '1',
    }

    startTime: string = ''
    endTime: string = ''
    isDataFetching: boolean = true
    extraTabs: string[] = []

    // =========================================================================
    // 逻辑方法
    // =========================================================================

    componentWillMount() {
        Global.isMobile = browserUtil.checkMobile()
    }

    componentDidMount() {
        const callChecker = async () => {
            const roleStr = localStorage.getItem('role') || `${ROLE.SUPER}`
            const role = roleStr === `${ROLE.SUPER}` ? ROLE.SUPER : ROLE.USER
            const page = role === ROLE.SUPER ? Page.dynamic : Page.userDynamic
            const isLogin = await checker.checkLogin(role, page)
            if (isLogin) {
                this.getDynamic()
            }
        }
        callChecker()
    }

    loadMore = () => {
        console.log(`Click load more...`)
        this.setState(
            { logPage: this.state.logPage + 1 },
            () => this.getDynamic()
        )
    }

    async getDynamic() {
        const DEBUG = false
        const TAG = `DynamicContent.getDynamic() - `
        this.isDataFetching = true
        const token = localStorage.getItem('token') || null
        const lang = localStorage.getItem('lang') || 'zh_CN'
        const role = localStorage.getItem('role') || `${ROLE.SUPER}`
        const requestData = {
            token,
            lang: lang.split('_')[0],
            sort: {
                order: 'DESC',
                field: 'created',
            },
            page: this.state.logPage,
            size: this.state.logSize,
            startTime: this.startTime,
            endTime: this.endTime,
            role: parseInt(role),
        }
        DEBUG && console.log(TAG, `requestData:\n`, requestData)
        const params = await encrypUtil.encodeRequestRsa(requestData)
        DEBUG && console.log(TAG, `params:\n`, params)
        const response = await axios.post(UserMgmtApi.GET_OP_LOG_LIST, params)
        this.isDataFetching = false
        DEBUG && console.log(TAG, `response:\n`, response)
        if (response.data.code === 200) {
            DEBUG && console.log(TAG, `获取日志列表:\n`, response.data.data)
            this.updateList(response.data.data)
        } else if (response.data.code === 400) {
            this.setState({
                tree: [],
                errCode: response.data.errCode,
            })
            const message = response.data.message
            console.error(TAG, `错误信息:\n`, message)
            const { t } = this.props
            this.showWarn(t('authError'), message)
        }
    }

    updateList = (data: any) => {
        if (data) {
            const totalRecords = data.totalRecords || 0
            if (data.list) {
                const newList = this.state.list.concat(data.list)
                this.setState({ list: newList, totalRecords }, () => {
                    this.formatDynamicList(newList)
                })
                return
            }
        }
        this.setState({ tree: [], totalRecords: 0 })
    }

    formatDynamicList = (list: any[]) => {
        const tree: DynamicTreeNode[] = []
        let treeNode: DynamicTreeNode | null = null
        for (let i = 0; i < list.length; i++) {
            const dynamic = list[i]
            let uname = `anonymous`
            if (dynamic.uname && dynamic.uname.length > 0) uname = dynamic.uname
            const dynamicItem: DynamicItem = {
                opTime: dynamic.opTime,
                uname,
                email: dynamic.email,
                api: dynamic.api,
                params: dynamic.params,
                comment: dynamic.comment,
            }
            if (!treeNode || treeNode.date !== dynamic.opDate) {
                treeNode = new DynamicTreeNode(dynamic.opDate, [dynamicItem])
                tree.push(treeNode)
            } else {
                treeNode.addDynamic(dynamicItem)
            }
        }
        this.setState({ tree })
    }

    /** 日期标签切换后的响应方法 */
    onDateChange = (key: string) => {
        console.log(key)
        this.setState({
            tree: [],
            list: [],
            logPage: 1,
            activeDate: key,
        })
        const now = new Date()
        switch (key) {
            case TAB_KEY_DATE.ALL:
                this.startTime = ''
                this.endTime = ''
                break
            case TAB_KEY_DATE.TODAY:
                this.startTime = timeUtil.formatDate(now, 'YYYY-MM-DD 00:00:00')
                this.endTime = timeUtil.formatDate(now, 'YYYY-MM-DD 23:59:59')
                break
            case TAB_KEY_DATE.YESTERDAY: {
                const yesterday = timeUtil.getYesterday(new Date())
                this.startTime = timeUtil.formatDate(yesterday, 'YYYY-MM-DD 00:00:00')
                this.endTime = timeUtil.formatDate(yesterday, 'YYYY-MM-DD 23:59:59')
                break
            }
            case TAB_KEY_DATE.THIS_WEEK:
                this.startTime = timeUtil.formatDate(timeUtil.getFirstDayOfWeek(now), 'YYYY-MM-DD 00:00:00')
                this.endTime = timeUtil.formatDate(timeUtil.getLastDayOfWeek(now), 'YYYY-MM-DD 23:59:59')
                break
            case TAB_KEY_DATE.LAST_WEEK: {
                const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
                this.startTime = timeUtil.formatDate(timeUtil.getFirstDayOfWeek(weekAgo), 'YYYY-MM-DD 00:00:00')
                this.endTime = timeUtil.formatDate(timeUtil.getLastDayOfWeek(weekAgo), 'YYYY-MM-DD 23:59:59')
                break
            }
            case TAB_KEY_DATE.THIS_MONTH:
                this.startTime = timeUtil.formatDate(timeUtil.getFirstDayOfMonth(now), 'YYYY-MM-DD 00:00:00')
                this.endTime = timeUtil.formatDate(timeUtil.getLastDayOfMonth(now), 'YYYY-MM-DD 23:59:59')
                break
            case TAB_KEY_DATE.LAST_MONTH: {
                const monthAgo = timeUtil.getLastMonthSameDay(now)
                this.startTime = timeUtil.formatDate(timeUtil.getFirstDayOfMonth(monthAgo), 'YYYY-MM-DD 00:00:00')
                this.endTime = timeUtil.formatDate(timeUtil.getLastDayOfMonth(monthAgo), 'YYYY-MM-DD 23:59:59')
                break
            }
            default: {
                const date = key
                this.startTime = `${date} 00:00:00`
                this.endTime = `${date} 23:59:59`
                break
            }
        }
        this.getDynamic()
    }

    // =========================================================================
    // 界面渲染
    // =========================================================================

    render() {
        if (Global.isMobile) return this.renderMobile()
        else return this.renderPc()
    }

    renderMobile() {
        return this.renderPc()
    }

    showWarn = (title: string, info: string) => {
        this.setState({
            warnTitle: title,
            warnInfo: info,
            showWarn: true,
        })
    }

    closeWarn = () => {
        const TAG = `DynamicContent.closeWarn() - `
        console.log(TAG, `this.state.errCode:\n`, this.state.errCode)
        if (this.state.errCode > 0) {
            if (
                this.state.errCode === ERR_CODE.NULL_TOKEN ||
                this.state.errCode === ERR_CODE.TOKEN_EXPIRED
            ) {
                window.location.href = '/mgmt'
            }
        }
        this.setState({ showWarn: false })
    }

    openAddDateTabPanel = () => {
        console.log(`Click openAddDateTabPanel`)
        this.setState({ isModal3Open: true })
    }

    refresh = () => {
        this.setState({ tree: [], list: [], logPage: 1 }, () => this.getDynamic())
    }

    renderPc() {
        const { t } = this.props

        const items: TabsProps['items'] = this.initDateTabs()

        const operations = (
            <Space>
                <Button icon={<ReloadOutlined />} onClick={this.refresh}>
                    {t('refresh') || '刷新'}
                </Button>
                <Button icon={<PlusOutlined />} onClick={this.openAddDateTabPanel}>
                    {t('add-date-tab')}
                </Button>
            </Space>
        )

        const onEdit = (
            targetKey: React.MouseEvent | React.KeyboardEvent | string,
            action: 'add' | 'remove'
        ) => {
            if (action === 'remove') {
                this.removeDateTab(targetKey as TargetKey)
            }
        }

        return (
            <Content
                style={{
                    margin: '4px',
                    padding: '6px',
                    background: 'var(--content-bg)',
                    minHeight: '280px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {this.renderWarn()}
                {this.renderModalAddDateTab()}

                <Card
                    title={
                        <Space>
                            <HistoryOutlined style={{ color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>
                                {t('dynamicMgmt')}
                            </Title>
                            <Tag color="blue">
                                {this.state.totalRecords} {t('records') || '条记录'}
                            </Tag>
                        </Space>
                    }
                    style={{
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        background: 'var(--card-bg)',
                    }}
                    bodyStyle={{
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    <Tabs
                        hideAdd
                        tabBarExtraContent={operations}
                        type="editable-card"
                        defaultActiveKey="1"
                        activeKey={this.state.activeDate}
                        onEdit={onEdit}
                        items={items}
                        onChange={this.onDateChange}
                    />
                </Card>
            </Content>
        )
    }

    handleAddDateTabOk = (values: DateTab) => {
        console.log(`handleAddDateTabOk() - `, values)
        this.addDateTab(values.date)
        this.setState({ isModal3Open: false })
    }

    addDateTab = (date: string | undefined) => {
        if (!date) return
        const newActiveKey = `${date}`
        this.startTime = `${date} 00:00:00`
        this.endTime = `${date} 23:59:59`
        this.extraTabs?.push(`${date}`)
        this.setState(
            {
                tree: [],
                list: [],
                logPage: 1,
                activeDate: newActiveKey,
            },
            () => this.getDynamic()
        )
    }

    removeDateTab = (targetKey: TargetKey) => {
        const DEBUG = true
        const TAG = `DynamicContent.removeDateTab() - `
        DEBUG && console.log(TAG, `oldActiveKey:`, this.state.activeDate)
        if (this.extraTabs) {
            for (let i = 0; i < this.extraTabs.length; i++) {
                if (this.extraTabs[i] === targetKey) {
                    this.extraTabs.splice(i, 1)
                }
            }
        }
        this.startTime = ''
        this.endTime = ''
        this.setState(
            {
                tree: [],
                list: [],
                logPage: 1,
                activeDate: '1',
            },
            () => this.getDynamic()
        )
    }

    handleAddDateTabCancel = () => {
        this.setState({ isModal3Open: false })
    }

    /** 初始化日期标签页 */
    initDateTabs = () => {
        const { t } = this.props
        const items: TabsProps['items'] = []
        for (let i = 0; i < FIXED_DATE_ITEMS.length; i++) {
            const item = FIXED_DATE_ITEMS[i]
            items.push({
                key: item.key,
                label: t(item.label),
                children: this.renderDateDynamic(item.key),
                closable: false,
            })
        }
        if (this.extraTabs && this.extraTabs.length > 0) {
            for (let i = 0; i < this.extraTabs.length; i++) {
                items.push({
                    key: this.extraTabs[i],
                    label: this.extraTabs[i],
                    children: this.renderDateDynamic(this.extraTabs[i]),
                })
            }
        }
        return items
    }

    renderWarn() {
        const { t } = this.props
        return (
            <WarnModal
                isMobile={Global.isMobile}
                isOpen={this.state.showWarn}
                onRequestClose={this.closeWarn}
                title={t('error')}
                subtitle={this.state.warnTitle}
                confirm={t('confirm')}
                customStyles={warnModalStyles}
            >
                {this.state.warnInfo}
            </WarnModal>
        )
    }

    /* 新增日期选项卡弹框 */
    renderModalAddDateTab() {
        return (
            <ModalAddDateTab
                isModalOpen={this.state.isModal3Open}
                handleOk={this.handleAddDateTabOk}
                handleCancel={this.handleAddDateTabCancel}
            />
        )
    }

    renderDateDynamic(key: string = TAB_KEY_DATE.ALL) {
        if (key !== this.state.activeDate) {
            return <></>
        }
        const { t, i18n } = this.props
        if (this.isDataFetching) {
            return (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    <Spin tip={t('plsWaitDataReturn')} />
                </div>
            )
        }
        if (this.state.tree.length === 0) {
            return (
                <Empty description={t('no-data')} style={{ padding: '32px 0' }} />
            )
        }

        const lang = i18n.language === 'zh_CN' ? 'zh' : 'en'
        const collapseItems: CollapseProps['items'] = []
        console.log(`this.state.tree:\n`, this.state.tree)

        for (let i = 0; i < this.state.tree.length; i++) {
            const treeNode: DynamicTreeNode = this.state.tree[i]
            const timeLineItems = []

            for (let j = 0; j < treeNode.dynamicList.length; j++) {
                const dynamic = treeNode.dynamicList[j]
                const uname =
                    dynamic.uname === 'anonymous'
                        ? `${t('anonymous')}(${dynamic.email})`
                        : dynamic.uname
                const email = dynamic.email
                const api = dynamic.api
                const params = dynamic.params

                let commentJson: any = {}
                try {
                    commentJson = JSON.parse(dynamic.comment)
                } catch (e) {
                    commentJson = {}
                }
                const comment = this.getComment(
                    api,
                    params,
                    commentJson[lang] || dynamic.comment
                )

                timeLineItems.push({
                    color: 'green',
                    children: (
                        <span>
                            <span style={{ color: 'rgba(0,0,0,0.45)' }}>{dynamic.opTime}</span>
                            <Tooltip title={email}>
                                <span style={{ color: '#1890ff', cursor: 'pointer' }}>
                                    {` ${uname}`}
                                </span>
                            </Tooltip>
                            <span style={{ fontWeight: 'bold' }}>{` ${comment}`}</span>
                        </span>
                    ),
                })
            }

            collapseItems.push({
                key: `${i + 1}`,
                label: (
                    <Space>
                        <Tag color="blue">{treeNode.date}</Tag>
                        <span style={{ color: 'rgba(0,0,0,0.45)' }}>
                            {treeNode.dynamicList.length} {t('records') || '条'}
                        </span>
                    </Space>
                ),
                children: <Timeline mode="left" items={timeLineItems} />,
            })
        }

        return (
            <div style={{ height: 'calc(100vh - 220px)', overflowY: 'auto' }}>
                <Collapse items={collapseItems} defaultActiveKey={['1']} />
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    {this.renderMoreBtn()}
                </div>
            </div>
        )
    }

    renderMoreBtn() {
        if (this.state.totalRecords > this.state.list.length) {
            const { t } = this.props
            return (
                <Button type="primary" onClick={this.loadMore}>
                    {t('more')}
                </Button>
            )
        }
        return <></>
    }

    getComment(api: string, params: any, comment: string) {
        switch (api) {
            case DataApi.DEL_CSV:
                return `${comment}: ${params?.url}`
            default:
                return comment
        }
    }
}

export const DynamicContent = withTranslation()(_DynamicContent)